import { useState, useEffect } from "react";
import { getSchedule } from "../services/scheduleService";
import {
  Clock,
  Calendar,
  Tv,
  AlertCircle,
  X
} from "lucide-react";
import { Divider } from "antd";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/Logo.svg";
import background from "../assets/BG image.png";

const Schedules = () => {
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    date: "",
    time: "",
    channels: {},
  });
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const data = await getSchedule();
      setSchedule(data.data || {});
      setError(null);
    } catch (err) {
      console.error("Error fetching schedule:", err);
      setError("Failed to load schedule. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return "Date TBD";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "Time TBD";
    try {
      const [hours, minutes] = timeStr.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    } catch {
      return timeStr;
    }
  };

  const getSportIcon = (sport) => {
    const sportIcons = {
      Soccer: "⚽",
      Basketball: "🏀",
      "American Football": "🏈",
      Cricket: "🏏",
      Baseball: "⚾",
      Fighting: "🥊",
      Golf: "⛳",
      Hockey: "🏒",
      Tennis: "🎾",
      Volleyball: "🏐",
      Rugby: "🏉",
      Racing: "🏎️",
      Boxing: "🥊",
      MMA: "🥋",
    };
    return sportIcons[sport] || "🏆";
  };

  const getSportsList = () =>
    Object.keys(schedule).filter((sport) => sport !== "updatedAt");

  const filteredSchedule = () => {
    if (activeTab === "All") return schedule;
    return { [activeTab]: schedule[activeTab] };
  };

  //search
  const getFilteredSchedule = () => {
    const scheduleByTab = filteredSchedule();

    if (!searchQuery.trim()) return scheduleByTab;

    const lowerQuery = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(scheduleByTab).forEach(([sport, leagues]) => {
      if (sport === "updatedAt") return;

      const filteredLeagues = {};
      Object.entries(leagues).forEach(([league, tournaments]) => {
        const filteredTournaments = {};
        Object.entries(tournaments).forEach(([tournament, matches]) => {
          if (
            tournament.toLowerCase().includes(lowerQuery) ||
            league.toLowerCase().includes(lowerQuery) ||
            sport.toLowerCase().includes(lowerQuery)
          ) {
            filteredTournaments[tournament] = matches;
          }
        });
        if (Object.keys(filteredTournaments).length > 0) {
          filteredLeagues[league] = filteredTournaments;
        }
      });

      if (Object.keys(filteredLeagues).length > 0) {
        filtered[sport] = filteredLeagues;
      }
    });

    return filtered;
  };

  const isTokenExpired = () => {
    const token = localStorage.getItem("token");
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const now = Date.now() / 1000;
      return now > payload.exp;
    } catch {
      return true;
    }
  };

  const handleBack = () => {
    if (isTokenExpired()) {
      window.location.href = "https://protvguide.com";
    } else {
      window.location.reload();
    }
  };

  const handleLogin = () => {
    navigate('/');
  }

  const openModal = (match) => {
    const date = match.date ? formatDate(match.date) : "Date TBD";
    const time = match.time ? formatTime(match.time) : "Time TBD";
    const channels = match.channels || {};
    setModalData({ date, time, channels });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent"></div>
          <span className="text-gray-800 font-semibold text-lg">
            Loading schedule...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h3 className="font-bold text-red-700 text-xl mb-2">
            Error Loading Schedule
          </h3>
          <p className="text-sm text-red-600 mb-6">{error}</p>
          <button
            onClick={fetchSchedule}
            className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sportsList = getSportsList();
  const hasData = sportsList.length > 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="relative w-full min-h-[20vh] md:min-h-[200px]">
        <img
          src={background}
          alt="Background"
          className="absolute inset-0 w-full h-[20vh] md:min-h-[200px] object-cover"
        />
        <div className="relative max-w-6xl mx-auto py-2 px-4 md:py-4 flex items-center justify-between">
          <img onClick={handleBack} src={Logo} alt="SportGPT Logo" className="w-10 md:w-20 h-10 md:h-16 cursor-pointer" />

          {isTokenExpired() && (
            <button
              className="bg-black text-white p-2 md:px-6 rounded-md"
              onClick={handleLogin}
            >
              <span>Login</span>
            </button>
          )}
        </div>
        {/* search */}
        <div className="max-w-6xl mx-auto px-4 mt-8 flex justify-center">
          <div className="w-full sm:w-1/2 relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 md:py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-[#E61F26]"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mt-4">
            <button
              onClick={() => {
                setActiveTab("All");
                setSearchQuery("");
              }}
              className={`px-5 py-2.5 rounded-lg font-semibold whitespace-nowrap transition-all ${
                activeTab === "All"
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Sports
            </button>
            {sportsList.map((sport) => (
              <button
                key={sport}
                onClick={() => {
                  setActiveTab(sport);
                  setSearchQuery("");
                }}
                className={`px-5 py-2.5 rounded-lg font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${
                  activeTab === sport
                    ? "bg-red-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="text-xl">{getSportIcon(sport)}</span>
                {sport}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-[#E61F26] px-6 py-5 flex flex-row items-center gap-3 justify-start rounded-lg ">
          <div className="bg-white rounded-lg p-1 flex items-center justify-center">
            <img src={Logo} alt="SportGPT Logo" className="w-11 h-11" />
          </div>
          {isTokenExpired() ? (
            <h3 className="text-sm md:text-md font-semibold text-white">
              <span
                onClick={() => navigate("/")}
                className="underline cursor-pointer hover:text-gray-200 transition-colors"
              >
                Sign in
              </span>{" "}
              to chat about games or get Discord/Telegram autoposts.
            </h3>
          ) : (
            <h3 className="text-sm md:text-md font-semibold text-white">
              <span
                onClick={() => navigate("/chat")}
                className="underline cursor-pointer hover:text-gray-200 transition-colors"
              >
                Get back
              </span>{" "}
              to chat about games or get Discord/Telegram autoposts.
            </h3>
          )}
        </div>

        {!hasData ? (
          <div className="bg-gray-50 rounded-2xl p-16 text-center border border-gray-200">
            <div className="text-6xl mb-4">Calendar</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">
              No Schedule Available
            </h3>
            <p className="text-gray-500">
              Check back later for upcoming matches.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="mt-5 text-2xl font-semibold">
              Today’s Featured Matches
            </div>
            {Object.entries(getFilteredSchedule()).map(([sport, leagues]) => {
              if (sport === "updatedAt") return null;

              return (
                <div key={sport} className="space-y-4">
                  {/* {activeTab === "All" && (
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-4xl">{getSportIcon(sport)}</span>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {sport}
                      </h2>
                    </div>
                  )} */}

                  {Object.entries(leagues).map(([league, tournaments]) => (
                    <div key={league} className="">
                      <div className="flex items-center justify-center gap-3 mb-5">
                        <div className="h-[1px] w-32 md:w-48 bg-gradient-to-l from-[#94A3B8] to-white"></div>
                        <h3 className="text-md md:text-lg font-semibold text-center ">
                          {league}
                        </h3>
                        <div className="h-[1px] w-32 md:w-48 bg-gradient-to-r from-[#94A3B8] to-white"></div>
                      </div>

                      <div className="p-4 md:p-6 space-y-4 bg-white rounded-2xl border border-gray-300 overflow-hidden shadow-sm">
                        {Object.entries(tournaments).map(
                          ([tournament, matches]) => {
                            const matchEntries = Object.entries(matches);
                            if (matchEntries.length === 0) return null;

                            const date = matchEntries[1]
                              ? matchEntries[1][1]
                              : null;
                            const time = matchEntries[0]
                              ? matchEntries[0][1]
                              : null;
                            const channels = matchEntries[2]
                              ? matchEntries[2][1]
                              : null;

                            const hasDate = date && parseDate(date);
                            const hasTime = time && typeof time === "string";
                            const hasChannels =
                              channels &&
                              Object.keys(channels).some(
                                (region) =>
                                  Array.isArray(channels[region]) &&
                                  channels[region].length > 0
                              );

                            return (
                              <div
                                key={tournament}
                                className="space-y-3 border-b border-gray-200 pb-3 last:border-none"
                              >
                                <div className="flex flex-row items-center gap-3">
                                  <div className="flex md:flex-row md:items-center gap-3 md:justify-between"></div>
                                  <h4 className="text-left font-bold flex-1">
                                    {tournament}
                                  </h4>

                                  {time && (
                                    <span className="text-gray-700">
                                      {formatTime(time)}
                                    </span>
                                  )}

                                  {/* details button */}
                                  <div className="flex flex-wrap gap-2">
                                    {(hasDate || hasTime || hasChannels) && (
                                      <button
                                        onClick={() =>
                                          openModal({ date, time, channels })
                                        }
                                        className="flex items-center gap-2 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg border border-red-200 transition-colors"
                                      >
                                        <span className="text-sm font-medium text-red-700">
                                          View Details
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-600" />
                Match Details
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Date & Time (merged in one block) */}
              {(modalData.date !== "Date TBD" ||
                modalData.time !== "Time TBD") && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 bg-red-50 p-2 rounded-lg">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mt-0.5">
                      {modalData.date !== "Date TBD"
                        ? modalData.date
                        : "Date TBD"}
                    </p>
                  </div>
                </div>
              )}

              {/* Channels */}
              {Object.keys(modalData.channels).length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 bg-red-50 p-2 rounded-lg">
                      <Tv className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      Available Channels
                    </p>
                  </div>

                  <div className="pl-4 sm:pl-12 space-y-3">
                    {["US", "UK", "CA", "Other"].map((region) => {
                      const channels = modalData.channels[region];
                      if (!Array.isArray(channels) || channels.length === 0)
                        return null;

                      return (
                        <div key={region} className="space-y-1">
                          <p className="text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            {region}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {channels.map((channel, idx) => (
                              <span
                                key={idx}
                                className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full hover:bg-gray-200 transition-colors"
                              >
                                {channel}
                              </span>
                            ))}
                          </div>
                          <Divider />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fallback when no info */}
              {modalData.date === "Date TBD" &&
                modalData.time === "Time TBD" &&
                Object.keys(modalData.channels).length === 0 && (
                  <p className="text-center text-gray-500 italic">
                    No details available
                  </p>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;
